// Lucide-style icon registry.
//
// Per design.md ADR-4 we inline 24 SVG paths rather than pulling
// `@lucide/static` (which is unverified in the current npm registry).
// Each value is a `d=` string suitable for inlining into
// `<svg viewBox="0 0 24 24" stroke="currentColor" fill="none"
// stroke-width="2" stroke-linecap="round" stroke-linejoin="round">`.
//
// The visual sub-set was enumerated in
// `openspec/changes/ui-redesign-v2/exploration.md` against the
// OpenPencil artboard. Adding a new icon requires (a) copying the
// Lucide path, (b) extending the `IconName` union, (c) covering it in
// `icons.test.ts`.
//
// stroke="currentColor" lets consumers colour icons via CSS color.

export type IconName =
  | "home"
  | "host"
  | "container"
  | "storage"
  | "services"
  | "network"
  | "audit"
  | "settings"
  | "restart"
  | "stop"
  | "play"
  | "search"
  | "filter"
  | "refresh"
  | "close"
  | "check"
  | "warning"
  | "error"
  | "info"
  | "clock"
  | "user"
  | "logout"
  | "menu"
  | "external-link";

export const ICONS: Readonly<Record<IconName, string>> = {
  home: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-3a1 1 0 0 1-1-1v-7h-6v7a1 1 0 0 1-1 1H5a2 2 0 0 1-2-2z",
  host: "M4 4h16v4H4zM4 10h16v4H4zM4 16h16v4H4z",
  container:
    "M21 8L12 3 3 8m18 0v8l-9 5-9-5V8m18 0l-9 5m0 0L3 8m9 5v8",
  storage:
    "M22 12H2m20 0v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-8m20 0V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v6m16-2h.01M6 10h.01",
  services:
    "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z",
  network:
    "M5 12a7 7 0 0 1 14 0M2 12a10 10 0 0 1 20 0M8.5 12a3.5 3.5 0 0 1 7 0M12 17.5v-5.5",
  audit:
    "M9 11l3 3 8-8M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11",
  settings:
    "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
  restart:
    "M1 4v6h6M23 20v-6h-6M3.51 15a9 9 0 1 0 2.13-9.36L1 10",
  stop: "M6 6h12v12H6z",
  play: "M5 3l14 9-14 9V3z",
  search: "M21 21l-4.35-4.35M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16z",
  filter: "M22 3H2l8 9.46V19l4 2v-8.54L22 3z",
  refresh:
    "M21 12a9 9 0 0 1-9 9 9 9 0 0 1-6.36-2.64L3 16M3 12a9 9 0 0 1 9-9 9 9 0 0 1 6.36 2.64L21 8M3 21v-5h5M21 3v5h-5",
  close: "M18 6L6 18M6 6l12 12",
  check: "M20 6L9 17l-5-5",
  warning:
    "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01",
  error: "M12 8v5M12 16.5v.5M2 2l20 20",
  info: "M12 16v-4M12 8h.01M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0z",
  clock: "M12 8v4l3 2M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20z",
  user:
    "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9",
  menu: "M3 12h18M3 6h18M3 18h18",
  "external-link":
    "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3",
};

export function getIcon(name: IconName): string | undefined {
  return ICONS[name];
}
